import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/currency';

// Dynamically load jsPDF libraries for exporting
const loadScript = (src) => {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script ${src}`));
        document.body.appendChild(script);
    });
};

const loadJsPdfLibraries = async () => {
    try {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
        return window.jspdf;
    } catch (error) {
        console.error("Failed to load jsPDF libraries:", error);
        return null;
    }
};

/**
 * Custom Multi-Client Select Component
 * A searchable, multi-select dropdown component.
 */
const MultiClientSelect = ({ clients, selectedClients, onChange, onSearch, searchTerm }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // --- MODIFICATION: Use robust getClientName function ---
    // This logic properly handles both Organizational and Individual clients,
    // and prevents display issues from null/undefined name fields.
    const getClientName = useCallback((client) => {
        if (!client) return '';
        // Use organization_name if available (for OrganizationalClient)
        if (client.organization_name) {
            return client.organization_name;
        }
        // Use first_name and last_name if available (for IndividualClient)
        if (client.first_name || client.last_name) {
            return `${client.first_name || ''} ${client.last_name || ''}`.trim();
        }
        // Fallback for clients that might be base model or have no name
        return 'Unnamed Client';
    }, []);
    // --- END MODIFICATION ---

    const toggleClient = (client) => {
        if (selectedClients.find(c => c.id === client.id)) {
            onChange(selectedClients.filter(c => c.id !== client.id));
        } else {
            onChange([...selectedClients, client]);
        }
    };

    // --- MODIFICATION: Filter using the robust getClientName function ---
    const filteredClients = useMemo(() => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        if (!lowerSearchTerm) return clients; // Show all if no search
        
        return (clients || []).filter(client => {
            const name = getClientName(client).toLowerCase();
            return name.includes(lowerSearchTerm);
        });
    }, [clients, searchTerm, getClientName]);
    // --- END MODIFICATION ---

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700">Clients (for Ledger)</label>
            <div
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 bg-white border cursor-pointer min-h-[38px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                {/* --- MODIFICATION: Updated placeholder text --- */}
                {selectedClients.length === 0 && <span className="text-gray-500">All Clients (Default)</span>}
                {/* --- END MODIFICATION --- */}
                {selectedClients.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {selectedClients.map(client => (
                            <span key={client.id} className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full flex items-center">
                                {getClientName(client)}
                                <button
                                    type="button"
                                    className="ml-1 text-blue-500 hover:text-blue-700"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent dropdown from closing
                                        toggleClient(client);
                                    }}
                                >
                                    &times;
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg border border-gray-200 rounded-md max-h-60 overflow-y-auto">
                    {/* --- MODIFICATION: Added relative container and clear button --- */}
                    <div className="relative w-full">
                        <input
                            type="text"
                            placeholder="Search clients..."
                            className="w-full p-2 border-b border-gray-200 sticky top-0"
                            value={searchTerm}
                            onChange={(e) => onSearch(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="absolute right-2 top-0 h-full flex items-center px-2 text-gray-500 hover:text-gray-800"
                                onClick={() => onSearch('')}
                                aria-label="Clear search"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        )}
                    </div>
                    {/* --- END MODIFICATION --- */}
                    
                    {/* --- MODIFICATION: Removed py-1 for cleaner look with search bar --- */}
                    <ul>
                        {filteredClients.length > 0 ? filteredClients.map(client => (
                            <li
                                key={client.id}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center"
                                onClick={() => toggleClient(client)}
                            >
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    checked={!!selectedClients.find(c => c.id === client.id)}
                                    readOnly
                                />
                                {getClientName(client)}
                            </li>
                        )) : (
                            <li className="px-3 py-2 text-gray-500">No clients found.</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};


/**
 * Sortable Table Header Component
 */
const SortableTh = ({ children, name, sortConfig, onSort }) => {
    const isSorted = sortConfig.key === name;
    const directionIcon = isSorted ? (sortConfig.direction === 'ascending' ? ' ▲' : ' ▼') : '';

    return (
        <th
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
            onClick={() => onSort(name)}
        >
            {children}{directionIcon}
        </th>
    );
};

/**
 * Custom hook for sorting table data
 */
const useSortableData = (items, config = { key: '', direction: '' }) => {
    const [sortConfig, setSortConfig] = useState(config);

    const sortedItems = useMemo(() => {
        let sortableItems = [...items];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [items, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };
    
    // Return sorted items as 'items' for consistency
    return { items: sortedItems, requestSort, sortConfig };
};

// --- Individual Report Components ---

// Use shared formatCurrency from utils/currency
// For negative display: formatCurrency handles negative values natively
const formatDate = (dateStr) => {
    try {
        // Handle potential timezone issues by parsing as local
        const date = new Date(dateStr + 'T00:00:00');
        if (isNaN(date.getTime())) return 'Invalid Date';
        return format(date, 'yyyy-MM-dd');
    } catch (e) {
        return 'Invalid Date';
    }
};

const ReportIncomeStatement = ({ data }) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
        <div>
            <h5 className="text-sm font-medium text-gray-500 uppercase">Total Revenue</h5>
            <p className="mt-1 text-3xl font-semibold text-green-600">{formatCurrency(data.total_revenue)}</p>
        </div>
        <div>
            <h5 className="text-sm font-medium text-gray-500 uppercase">Total Expenses</h5>
            <p className="mt-1 text-3xl font-semibold text-red-600">{formatCurrency(data.total_expenses)}</p>
        </div>
        <div>
            <h5 className="text-sm font-medium text-gray-500 uppercase">Net Income</h5>
            <p className={`mt-1 text-3xl font-semibold ${data.net_income >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {formatCurrency(data.net_income)}
            </p>
        </div>
    </div>
);

const ReportAgedReceivables = ({ data }) => {
    const allInvoices = Object.entries(data).flatMap(([bucket, invoices]) =>
        invoices.map(inv => ({ ...inv, bucket: bucket.replace('_', ' ') }))
    );
    const { items: sortedInvoices, requestSort, sortConfig } = useSortableData(allInvoices, { key: 'due_date', direction: 'ascending' });

    return (
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <SortableTh name="bucket" sortConfig={sortConfig} onSort={requestSort}>Aging Bucket</SortableTh>
                    <SortableTh name="client_name" sortConfig={sortConfig} onSort={requestSort}>Client</SortableTh>
                    <SortableTh name="invoice_number" sortConfig={sortConfig} onSort={requestSort}>Invoice #</SortableTh>
                    <SortableTh name="due_date" sortConfig={sortConfig} onSort={requestSort}>Due Date</SortableTh>
                    <SortableTh name="balance_due" sortConfig={sortConfig} onSort={requestSort}>Balance Due</SortableTh>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {sortedInvoices.map((inv) => (
                    <tr key={inv.invoice_number}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inv.bucket}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{inv.client_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{inv.invoice_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(inv.due_date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(inv.balance_due)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const ReportTrustSummary = ({ data, onAccountClick }) => {
    const { items: sortedAccounts, requestSort, sortConfig } = useSortableData(data, { key: 'client_name', direction: 'ascending' });
    const totalBalance = useMemo(() => {
        return (sortedAccounts || []).reduce((sum, acc) => sum + (Number(acc?.balance) || 0), 0);
    }, [sortedAccounts]);

    const handleRowActivate = (acc) => {
        if (!onAccountClick) return;
        onAccountClick(acc);
    };

    return (
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <SortableTh name="account_name" sortConfig={sortConfig} onSort={requestSort}>Account</SortableTh>
                    <SortableTh name="client_name" sortConfig={sortConfig} onSort={requestSort}>Client</SortableTh>
                    <SortableTh name="balance" sortConfig={sortConfig} onSort={requestSort}>Current Balance</SortableTh>
                    <SortableTh name="deposits_in_range" sortConfig={sortConfig} onSort={requestSort}>Deposits in Range</SortableTh>
                    <SortableTh name="withdrawals_in_range" sortConfig={sortConfig} onSort={requestSort}>Withdrawals in Range</SortableTh>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {sortedAccounts.map((acc, index) => (
                    <tr
                        key={acc.account_id || index}
                        className={onAccountClick ? 'hover:bg-gray-50 cursor-pointer' : undefined}
                        onClick={() => handleRowActivate(acc)}
                        role={onAccountClick ? 'button' : undefined}
                        tabIndex={onAccountClick ? 0 : undefined}
                        onKeyDown={(e) => {
                            if (!onAccountClick) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRowActivate(acc);
                            }
                        }}
                    >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{acc.account_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{acc.client_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(acc.balance)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right">{formatCurrency(acc.deposits_in_range)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-right">-{formatCurrency(Math.abs(acc.withdrawals_in_range))}</td>
                    </tr>
                ))}
            </tbody>
            <tfoot className="bg-gray-100 font-bold">
                <tr>
                    <td colSpan={2} className="px-6 py-3 text-right text-sm text-gray-700 uppercase">Total</td>
                    <td className="px-6 py-3 text-right text-sm text-gray-700">{formatCurrency(totalBalance)}</td>
                    <td className="px-6 py-3 text-right text-sm text-gray-700"></td>
                    <td className="px-6 py-3 text-right text-sm text-gray-700"></td>
                </tr>
            </tfoot>
        </table>
    );
};

const ReportTrialBalance = ({ data }) => {
    const { items: sortedAccounts, requestSort, sortConfig } = useSortableData(data.accounts, { key: 'account_name', direction: 'ascending' });
    return (
        <>
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <SortableTh name="account_code" sortConfig={sortConfig} onSort={requestSort}>Account Code</SortableTh>
                        <SortableTh name="account_name" sortConfig={sortConfig} onSort={requestSort}>Account Name</SortableTh>
                        <SortableTh name="debit" sortConfig={sortConfig} onSort={requestSort}>Debit</SortableTh>
                        <SortableTh name="credit" sortConfig={sortConfig} onSort={requestSort}>Credit</SortableTh>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {sortedAccounts.map((acc, index) => (
                        <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{acc.account_code}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{acc.account_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(acc.debit)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(acc.credit)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="bg-gray-100 font-bold">
                    <tr>
                        <td colSpan="2" className="px-6 py-3 text-right text-sm text-gray-700 uppercase">Totals</td>
                        <td className="px-6 py-3 text-right text-sm text-gray-700">{formatCurrency(data.total_debits)}</td>
                        <td className="px-6 py-3 text-right text-sm text-gray-700">{formatCurrency(data.total_credits)}</td>
                    </tr>
                </tfoot>
            </table>
            {data.total_debits !== data.total_credits && (
                <p className="text-red-600 font-bold text-center mt-4">Warning: Debits and Credits do not match!</p>
            )}
        </>
    );
};

const ReportBalanceSheet = ({ data }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Assets Section */}
        <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2 border-b pb-1">Assets</h4>
            {Object.entries(data.assets).map(([category, accounts]) => (
                <div key={category} className="mb-4">
                    <h5 className="text-md font-medium text-gray-600 capitalize">{category.replace('_', ' ')}</h5>
                    <ul className="divide-y divide-gray-100 ml-4">
                        {accounts.items.map((item, idx) => (
                            <li key={idx} className="flex justify-between py-1 text-sm">
                                <span>{item.account_name}</span>
                                <span>{formatCurrency(item.balance)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="flex justify-between font-semibold text-sm border-t pt-1 ml-4">
                        <span>Total {category.replace('_', ' ')}</span>
                        <span>{formatCurrency(accounts.total)}</span>
                    </div>
                </div>
            ))}
            <div className="flex justify-between font-bold text-lg border-t-2 border-gray-800 pt-2 mt-4">
                <span>Total Assets</span>
                <span>{formatCurrency(data.total_assets)}</span>
            </div>
        </div>

        {/* Liabilities & Equity Section */}
        <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2 border-b pb-1">Liabilities & Equity</h4>
            {Object.entries(data.liabilities).map(([category, accounts]) => (
                <div key={category} className="mb-4">
                    <h5 className="text-md font-medium text-gray-600 capitalize">{category.replace('_', ' ')}</h5>
                    <ul className="divide-y divide-gray-100 ml-4">
                        {accounts.items.map((item, idx) => (
                            <li key={idx} className="flex justify-between py-1 text-sm">
                                <span>{item.account_name}</span>
                                <span>{formatCurrency(item.balance)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="flex justify-between font-semibold text-sm border-t pt-1 ml-4">
                        <span>Total {category.replace('_', ' ')}</span>
                        <span>{formatCurrency(accounts.total)}</span>
                    </div>
                </div>
            ))}
            {/* Equity Section */}
            <div className="mb-4 mt-8">
                <h5 className="text-md font-medium text-gray-600">Equity</h5>
                <ul className="divide-y divide-gray-100 ml-4">
                    {data.equity.items.map((item, idx) => (
                        <li key={idx} className="flex justify-between py-1 text-sm">
                            <span>{item.account_name}</span>
                            <span>{formatCurrency(item.balance)}</span>
                        </li>
                    ))}
                </ul>
                <div className="flex justify-between font-semibold text-sm border-t pt-1 ml-4">
                    <span>Total Equity</span>
                    <span>{formatCurrency(data.equity.total)}</span>
                </div>
            </div>
            
            <div className="flex justify-between font-bold text-lg border-t-2 border-gray-800 pt-2 mt-4">
                <span>Total Liabilities & Equity</span>
                <span>{formatCurrency(data.total_liabilities_and_equity)}</span>
            </div>
        </div>
    </div>
);

const ReportGeneralLedger = ({ data }) => {
    const { items: sortedTransactions, requestSort, sortConfig } = useSortableData(data, { key: 'date', direction: 'ascending' });
    return (
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <SortableTh name="date" sortConfig={sortConfig} onSort={requestSort}>Date</SortableTh>
                    <SortableTh name="client_name" sortConfig={sortConfig} onSort={requestSort}>Client</SortableTh>
                    <SortableTh name="account_name" sortConfig={sortConfig} onSort={requestSort}>Account</SortableTh>
                    <SortableTh name="description" sortConfig={sortConfig} onSort={requestSort}>Description</SortableTh>
                    <SortableTh name="debit" sortConfig={sortConfig} onSort={requestSort}>Debit</SortableTh>
                    <SortableTh name="credit" sortConfig={sortConfig} onSort={requestSort}>Credit</SortableTh>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {sortedTransactions.map((tx, index) => (
                    <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(tx.date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tx.client_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tx.account_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tx.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(tx.debit)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(tx.credit)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

// --- MODIFIED: Billable Hours/Revenue Report Component ---
const ReportBillableHoursRevenue = ({ data }) => {
    // Data from backend is { hours_by_user: [...], revenue_by_client: [...] }
    const { hours_by_user = [], revenue_by_client = [] } = data;

    const { items: sortedHours, requestSort: requestSortHours, sortConfig: sortConfigHours } = useSortableData(hours_by_user, { key: 'user', direction: 'ascending' });
    const { items: sortedRevenue, requestSort: requestSortRevenue, sortConfig: sortConfigRevenue } = useSortableData(revenue_by_client, { key: 'client', direction: 'ascending' });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
                <h5 className="text-lg font-semibold text-gray-800 mb-4">Billable Hours by User</h5>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <SortableTh name="user" sortConfig={sortConfigHours} onSort={requestSortHours}>Staff Member</SortableTh>
                            <SortableTh name="hours" sortConfig={sortConfigHours} onSort={requestSortHours}>Total Billable Hours</SortableTh>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedHours.map((entry, index) => (
                            <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.user}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{parseFloat(entry.hours || 0).toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div>
                <h5 className="text-lg font-semibold text-gray-800 mb-4">Revenue by Client</h5>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <SortableTh name="client" sortConfig={sortConfigRevenue} onSort={requestSortRevenue}>Client</SortableTh>
                            <SortableTh name="revenue" sortConfig={sortConfigRevenue} onSort={requestSortRevenue}>Total Revenue</SortableTh>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedRevenue.map((entry, index) => (
                            <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.client}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(entry.revenue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// --- MODIFIED: Cash Flow Statement Component ---
const ReportCashFlowStatement = ({ data }) => {
    // Data from backend: { operating_cash_flow: { items: [...], total: ... }, ..., net_cash_flow: ... }
    const { operating_cash_flow = { items: [], total: 0 }, net_cash_flow } = data;
    
    return (
        <div className="max-w-2xl mx-auto">
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Operating Activities</h3>
                    <div className="mt-4 flow-root">
                        <ul className="divide-y divide-gray-200">
                            {operating_cash_flow.items.map((item, index) => (
                                <li key={index} className="py-3 flex justify-between text-sm">
                                    <span className="text-gray-600">{item.name}</span>
                                    <span className={`font-medium ${item.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {item.amount < 0 ? `(${formatCurrency(Math.abs(item.amount))})` : formatCurrency(item.amount)}
                                    </span>
                                </li>
                            ))}
                            <li className="py-3 flex justify-between text-sm font-semibold border-t">
                                <span className="text-gray-800">Net Cash Flow from Operating Activities</span>
                                <span className="text-gray-900">{formatCurrency(operating_cash_flow.total)}</span>
                            </li>
                        </ul>
                    </div>
                    {/* Placeholder for Investing and Financing activities can be added here */}
                </div>
                <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t">
                    <div className="flex justify-between text-base font-semibold">
                        <span className="text-gray-900">Net Cash Flow</span>
                        <span className={`text-xl ${net_cash_flow >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            {formatCurrency(net_cash_flow)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};


// --- Main AnnualAuditSupport Component ---

const AnnualAuditSupport = ({ authAxios, clients, showFormMessage, userRole, navigateToTrustLedger, initialStartDate, initialEndDate, initialActiveReport }) => {
    // --- RBAC Permissions ---
    const canViewAuditSupport = userRole === 'PARTNER' || userRole === 'BOARD_MEMBER' || userRole === 'SUPERUSER';

    const [startDate, setStartDate] = useState(initialStartDate || '');
    const [endDate, setEndDate] = useState(initialEndDate || '');
    
    // --- NEW LOGIC: State for this component's own client list ---
    const [ledgerClients, setLedgerClients] = useState(clients || []);
    const [selectedLedgerClients, setSelectedLedgerClients] = useState([]);
    const [ledgerClientSearch, setLedgerClientSearch] = useState('');
    // --- END NEW LOGIC ---
    
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [jsPdfLoaded, setJsPdfLoaded] = useState(false);
    const [printing, setPrinting] = useState(false);

    const reportContainerRef = useRef(null);

    const [activeReport, setActiveReport] = useState(initialActiveReport || 'profit_loss_statement');

    useEffect(() => {
        if (initialStartDate) setStartDate(initialStartDate);
        if (initialEndDate) setEndDate(initialEndDate);
        if (initialActiveReport) setActiveReport(initialActiveReport);
        // Only react to external navigation changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialStartDate, initialEndDate, initialActiveReport]);

    // --- MODIFIED: Corrected dataKey for billable_hours_revenue ---
    const reportOptions = [
        { key: 'profit_loss_statement', label: 'Profit & Loss Statement', dataKey: 'income_statement' },
        { key: 'balance_sheet', label: 'Balance Sheet', dataKey: 'balance_sheet' },
        { key: 'cash_flow_statement', label: 'Cash Flow Statement', dataKey: 'cash_flow_statement' },
        { key: 'aged_receivables', label: 'Aged Receivables', dataKey: 'aged_receivables' },
        { key: 'trust_summary', label: 'Trust Account Summary', dataKey: 'trust_account_summary' },
        { key: 'billable_hours_revenue', label: 'Billable Hours/Revenue', dataKey: 'billable_revenue_report' }, // <-- Corrected dataKey
    ];
    // --- END MODIFICATION ---

    // --- Use clients prop directly ---
    useEffect(() => {
        setLedgerClients(clients || []);
    }, [clients]);
    
    useEffect(() => {
        loadJsPdfLibraries().then(pdfModule => {
            if (pdfModule) {
                setJsPdfLoaded(true);
            }
        });

        const handleAfterPrint = () => setPrinting(false);
        window.addEventListener('afterprint', handleAfterPrint);
        return () => window.removeEventListener('afterprint', handleAfterPrint);
    }, []);
    // --- END ---
    
    const handleGenerateReport = async () => {
        if (!startDate || !endDate) {
            showFormMessage('danger', 'Please select both a start and end date.');
            return;
        }
        setLoading(true);
        setReportData(null);
        try {
            const params = {
                start_date: startDate,
                end_date: endDate,
                // --- MODIFICATION: Use new component-level state for client IDs ---
                client_ids: selectedLedgerClients.map(c => c.id).join(','),
            };
            // --- MODIFICATION: Updated endpoint to match views.py ---
            const response = await authAxios.get('reports/annual-audit/', { params });
            setReportData(response.data);
            setActiveReport(initialActiveReport || 'profit_loss_statement');
        } catch (error) {
            console.error("Failed to generate audit report:", error);
            showFormMessage('danger', 'An error occurred while generating the report.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        setPrinting(true);
        setTimeout(() => {
            window.print();
        }, 100); // Allow time for state to update and re-render
    };

    const handleExportPDF = async () => {
        if (!jsPdfLoaded || !reportData || !reportContainerRef.current) {
            showFormMessage('danger', 'PDF libraries not loaded or no report data.');
            return;
        }

        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        const reportTitle = reportOptions.find(r => r.key === activeReport)?.label || 'Audit Report';
        const firmName = "Your Law Firm"; // This could come from props or API
        const period = `From ${startDate} to ${endDate}`;

        doc.setFontSize(18);
        doc.text(firmName, 14, 22);
        doc.setFontSize(14);
        doc.text(reportTitle, 14, 30);
        doc.setFontSize(10);
        doc.text(period, 14, 36);

        // Find all table elements to export
        const tableElements = reportContainerRef.current.querySelectorAll('table');
        
        if (tableElements.length > 0) {
             // Use autoTable for complex tables
            doc.autoTable({
                html: tableElements[0], // Start with the first table
                startY: 40,
                theme: 'grid',
                styles: { fontSize: 8 },
                headStyles: { fillColor: [27, 60, 83] }, // --primary-color
            });

            // If there's a second table (for Billable Hours report)
            if (tableElements.length > 1) {
                doc.autoTable({
                    html: tableElements[1],
                    startY: doc.autoTable.previous.finalY + 10, // Start after the first table
                    theme: 'grid',
                    styles: { fontSize: 8 },
                    headStyles: { fillColor: [27, 60, 83] },
                });
            }
        } else {
             // Handle non-table content (like Income Statement or Cash Flow)
             // This is a simplified text export. A canvas-based export (html2canvas) would be more accurate.
             const textContent = reportContainerRef.current.innerText;
             doc.setFontSize(10);
             doc.text(textContent, 14, 45, { maxWidth: 180 });
        }

        doc.save(`${reportTitle.replace(/ /g, '_')}_${startDate}_to_${endDate}.pdf`);
    };

    const renderActiveReport = () => {
        if (!reportData) return null;

        const reportKey = reportOptions.find(r => r.key === activeReport)?.dataKey;
        const data = reportData[reportKey];

        if (!data) {
            return <p className="text-gray-500 text-center">No data available for this report.</p>;
        }

        switch (activeReport) {
            case 'profit_loss_statement':
                return <ReportIncomeStatement data={data} />;
            case 'aged_receivables':
                return <ReportAgedReceivables data={data} />;
            case 'trust_summary':
                return (
                    <ReportTrustSummary
                        data={data}
                        onAccountClick={navigateToTrustLedger ? (acc) => {
                            if (!acc?.account_id) return;
                            navigateToTrustLedger(acc.account_id, { from: 'annualAuditSupport', startDate, endDate });
                        } : undefined}
                    />
                );
            case 'balance_sheet':
                return <ReportBalanceSheet data={data} />;
            case 'billable_hours_revenue':
                return <ReportBillableHoursRevenue data={data} />;
            case 'cash_flow_statement':
                return <ReportCashFlowStatement data={data} />;
            default:
                return <p className="text-gray-500 text-center">Report view not implemented.</p>;
        }
    };
    
    // --- Print Styles ---
    const printStyles = `
        @media print {
            body * { visibility: hidden; }
            .printable-area, .printable-area * { visibility: visible; }
            .printable-area { 
                position: absolute; 
                left: 0; 
                top: 0; 
                width: 100%;
                padding: 20px;
            }
            .no-print { display: none !important; }
            table { font-size: 10px; }
            .text-3xl { font-size: 24px !important; }
        }
    `;
    
    if (!canViewAuditSupport) {
        return (
            <div className="p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Access Denied: </strong>
                    <span className="block sm:inline">This feature is restricted to Partners, Board Members, and Superusers.</span>
                </div>
            </div>
        );
    }
    
    return (
        <div className={`p-4 animate__animated animate__fadeIn ${printing ? 'printable-area' : ''}`}>
            <style>{printStyles}</style>
            
            <div className="no-print">
                <h4 className="text-xl font-bold text-gray-700 mb-4">Annual Audit Support</h4>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6 no-print">
                <h5 className="text-lg font-semibold text-gray-800 mb-4">Report Filters</h5>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Start Date</label>
                        <input type="date" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">End Date</label>
                        <input type="date" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                    <div className="md:col-span-1">
                        {/* --- MODIFICATION: Wire up new state --- */}
                        <MultiClientSelect
                            clients={ledgerClients}
                            selectedClients={selectedLedgerClients}
                            onChange={setSelectedLedgerClients}
                            onSearch={setLedgerClientSearch}
                            searchTerm={ledgerClientSearch}
                        />
                    </div>
                    <div>
                        <button className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={handleGenerateReport} disabled={loading}>
                            {loading ? <span className="spinner-border spinner-border-sm"></span> : 'Generate'}
                        </button>
                    </div>
                </div>
            </div>

            {loading && <div className="text-center my-8 no-print"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div><p className="mt-3 text-gray-600">Generating reports...</p></div>}

            {reportData && (
                <div className="bg-white rounded-lg shadow-md">
                    {/* Report Tabs */}
                    <div className="border-b border-gray-200 no-print">
                        <nav className="-mb-px flex space-x-4 overflow-x-auto px-4" aria-label="Tabs">
                            {reportOptions.map((report) => (
                                reportData[report.dataKey] && ( // Only show tab if data exists
                                    <button
                                        key={report.key}
                                        onClick={() => setActiveReport(report.key)}
                                        className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                                            activeReport === report.key
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        {report.label}
                                    </button>
                                )
                            ))}
                        </nav>
                    </div>

                    {/* Report Content */}
                    <div id="report-content" className={printing ? '' : 'printable-area'}>
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h4 className="text-lg font-bold text-gray-800">
                                {reportOptions.find(r => r.key === activeReport)?.label}
                            </h4>
                            <div className="flex space-x-2 no-print">
                                <button 
                                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    onClick={handlePrint}
                                    disabled={printing}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                    Print
                                </button>
                                <button 
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                    onClick={handleExportPDF}
                                    disabled={!jsPdfLoaded}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                    Export PDF
                                </button>
                            </div>
                        </div>
                        <div className="p-6 overflow-x-auto" ref={reportContainerRef}>
                            {renderActiveReport()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnualAuditSupport;

